import { useState } from 'react';
import { nullSafeGet } from '../../utils/DataAccessUtils';
import { getDuplicateSearchKey, getSupplierMatchContactDetails } from './supplierRequestFormUtils';

interface UseSupplierDuplicateCheckArgs {
	/** The AntD form instance holding the supplier contact fields. */
	form: any;
	/** Dispatches the name + email supplier lookup; resolves with the matching supplier facilities. */
	searchSuppliers: (companyName: string, contactEmail: string) => Promise<any>;
	/**
	 * Resolves with the caller's private-network rows for the given supplier facility ids, so matches
	 * the company already works with can be flagged instead of being requested again.
	 */
	fetchNetworkMembership: (supplierFacilityIds: number[]) => Promise<any>;
	/** Called with the matches when the lookup finds similar suppliers the user should choose from. */
	onMatchesFound: (matches: any[]) => void;
	/** Called when there is nothing to choose from, or the user has already answered for these values. */
	onProceed: () => void;
}

/**
 * Duplicate-supplier pre-check shared by the requester form (`RequestToAddSupplierForm`) and the
 * approver form (`ApproveEntityAddRequestModal`). Both hit the same name + email lookup and both let
 * the user either pick an existing supplier or continue with the values they entered.
 *
 * The check is never blocking: a lookup failure, an empty result, or contact values we have already
 * prompted for all fall through to `onProceed`.
 */
const useSupplierDuplicateCheck = ({
	form,
	searchSuppliers,
	fetchNetworkMembership,
	onMatchesFound,
	onProceed,
}: UseSupplierDuplicateCheckArgs) => {
	const [matches, setMatches] = useState([]);
	// Ids (as strings) of the matches already in the caller's private network.
	const [existingSupplierIds, setExistingSupplierIds] = useState([]);
	// The contact values the user has already been answered for — changing either value re-runs the
	// check. Showing the matches step is NOT an answer: the user has to pick a match or decline first,
	// otherwise Back → Next would skip straight past the picker for the same values.
	const [answeredKey, setAnsweredKey] = useState(undefined);
	const [checking, setChecking] = useState(false);
	// The existing supplier facility picked out of the matches, if any. The approver flow assigns this
	// facility to the private network instead of creating another supplier for the same company.
	const [selectedSupplier, setSelectedSupplier] = useState(undefined);

	const checkForDuplicates = () => {
		const values = form.getFieldsValue(true);
		const searchKey = getDuplicateSearchKey(values);
		if (answeredKey === searchKey) {
			onProceed();
			return;
		}
		setChecking(true);
		searchSuppliers(values.companyName, values.contactEmail)
			.then((foundSuppliers) => {
				if (!foundSuppliers || foundSuppliers.length === 0) {
					setChecking(false);
					// Nothing to pick for these contact values — record them as answered so an unchanged
					// re-check doesn't re-issue the same search, and drop a supplier picked for earlier ones.
					setAnsweredKey(searchKey);
					setMatches([]);
					setExistingSupplierIds([]);
					setSelectedSupplier(undefined);
					onProceed();
					return undefined;
				}
				// Which of the matches is the company already working with? Resolved before the picker
				// opens so the rows are flagged from the start. A failed membership lookup just means no
				// flags — it must never block the flow.
				return fetchNetworkMembership(foundSuppliers.map((supplier) => supplier.id))
					.catch(() => [])
					.then((networkRows) => {
						setChecking(false);
						setExistingSupplierIds(
							(networkRows || []).map((networkRow) =>
								String(nullSafeGet('supplierFacilityId', networkRow))
							)
						);
						setMatches(foundSuppliers);
						onMatchesFound(foundSuppliers);
					});
			})
			.catch(() => {
				// A failed lookup must never block the flow — just move on.
				setChecking(false);
				onProceed();
			});
	};

	/**
	 * Fill the contact fields from a supplier the user picked out of the matches, keeping whatever they
	 * typed for any detail the matched facility doesn't carry. The filled values become the answered
	 * key so they don't immediately trigger the prompt again.
	 */
	const applyMatchToForm = (supplier) => {
		const matchDetails = getSupplierMatchContactDetails(supplier);
		const currentValues = form.getFieldsValue(true);
		const filledValues = {
			companyName: matchDetails.companyName || currentValues.companyName,
			contactEmail: matchDetails.contactEmail || currentValues.contactEmail,
			contactFirstName: matchDetails.contactFirstName || currentValues.contactFirstName,
			contactLastName: matchDetails.contactLastName || currentValues.contactLastName,
		};
		form.setFieldsValue(filledValues);
		setAnsweredKey(getDuplicateSearchKey(filledValues));
		setSelectedSupplier(supplier);
	};

	/** "No, use the supplier I entered" — the contact values stand on their own. */
	const clearSelectedSupplier = () => setSelectedSupplier(undefined);

	return {
		matches,
		existingSupplierIds,
		checking,
		selectedSupplier,
		checkForDuplicates,
		applyMatchToForm,
		clearSelectedSupplier,
	};
};

export default useSupplierDuplicateCheck;
